import { lazy } from 'react';

/**
 * Route-level code splitting with ONE safety net.
 *
 * Every page used to be imported eagerly, so the projects page paid for the analytics
 * charts, the HLS player, the emoji picker and every admin screen before it could
 * draw (3.4 MB raw / 922 KB gzipped, measured 2026-09-11). lazyPage() splits a page
 * into its own chunk, loaded on first navigation.
 *
 * The one failure mode this creates: a tab left open across a deploy asks for a chunk
 * the new build no longer serves (the hash changed), and React.lazy rejects with a
 * ChunkLoadError. Instead of the error screen, reload ONCE so the tab picks up the new
 * build; a flag in sessionStorage stops a broken build from reloading forever. The
 * decision is a pure function so it can be tested without a network.
 */
const RELOAD_FLAG = 'ug_chunk_reload';

export function isChunkLoadError(err) {
  const name = err?.name || '';
  const msg = String(err?.message || '');
  return name === 'ChunkLoadError' || /Loading (CSS )?chunk [\w-]+ failed/i.test(msg) || /Failed to fetch dynamically imported module/i.test(msg);
}

/** -> 'reload' (first failure this session) | 'throw' (already reloaded once, or not a chunk error). */
export function decideOnImportError(err, storage) {
  if (!isChunkLoadError(err)) return 'throw';
  let already = null;
  try { already = storage.getItem(RELOAD_FLAG); } catch { /* storage unavailable: never loop */ return 'throw'; }
  if (already) return 'throw';
  try { storage.setItem(RELOAD_FLAG, String(Date.now())); } catch { return 'throw'; }
  return 'reload';
}

/** Clear the once-only flag after a successful import so a LATER deploy gets its own reload. */
export function clearReloadFlag(storage) {
  try { storage.removeItem(RELOAD_FLAG); } catch { /* ignore */ }
}

export default function lazyPage(importer, { storage, reload } = {}) {
  const st = storage || (typeof window !== 'undefined' ? window.sessionStorage : null);
  const doReload = reload || (() => window.location.reload());
  return lazy(() => importer().then((mod) => { if (st) clearReloadFlag(st); return mod; })
                              .catch((err) => {
                                if (st && decideOnImportError(err, st) === 'reload') {
                                  doReload();
                                  return new Promise(() => {});   // the reload takes over; never resolve
                                }
                                throw err;                        // the ErrorBoundary shows its screen
                              }));
}
